import { Injectable, Injector } from '@angular/core';
import { UserHelperService } from './user-helper.service';
import { BehaviorSubject, firstValueFrom } from 'rxjs';
import { AuthenticationService } from '@modules/authentication/authentication.service';
import { UserPreferencesService } from '@modules/users/user.preferences.service';
import { AclAbilityService } from '@services/acl/acl.ability';
import { Router } from '@angular/router';
import { RoutesEnum } from '@enums/routes.enum';
import { ChatSocketService } from '@events/chat/chat-socket.service';
import { UserAttributesI } from '@interfaces/user.interface';
import { RefreshTokenDataI, SignOutDataI } from '@interfaces/auth.interface';
@Injectable({
  providedIn: 'root',
})
export class AdminOtherUsersService {
  constructor(
    private userHelperService: UserHelperService,
    private authService: AuthenticationService,
    private userPreferencesService: UserPreferencesService,
    private aclService: AclAbilityService,
    private router: Router,
    private injector: Injector
  ) {
    if (this.getLoginOtherProcessing()) {
      //ficou processando , desloga
      const atts = this.userHelperService.getUserAttributes() ?? {};
      setTimeout(() => {
        this.authService.signout(atts['custom:userId']);
      }, 1000);
    }
    this.setShoudReload(false);
    window.addEventListener('storage', (event) => {
      if (this.getShoudReload()) {
        this.setShoudReload(false);
        this.reloadPage();
      }
    });
  }

  public isUsingOtherUser(): boolean {
    return this.userHelperService.isUsingOtherUser();
  }

  public async loginAs(userIdOther: string, onEndLoad?: Function) {
    try {
      const atts = this.userHelperService.getUserAttributes();
      const userId = atts['custom:userId'];
      const response = await firstValueFrom(this.authService.signinOther(userId, userIdOther));
      const hasResponse = !!response;
      onEndLoad!(hasResponse);
      if (hasResponse) {
        await this.overrideUserCache(response);
        this.setToReload();
      }
    } catch (err) {
      onEndLoad!(false);
    }
  }
  private reloadPage() {
    document.location.href = '/';
  }
  private setToReload() {
    this.setShoudReload(true); //use storage event to reload all tabs
    this.reloadPage();
  }

  private setLoginLogoutOtherProcessing(value) {
    if (!value) {
      localStorage.removeItem('signin_other_result_pending');
      return;
    }
    localStorage.setItem('signin_other_result_pending', value.toString());
  }

  private getLoginOtherProcessing() {
    return localStorage.getItem('signin_other_result_pending') === 'true';
  }

  private setShoudReload(value) {
    if (!value) {
      localStorage.removeItem('should_reload');
      return;
    }
    localStorage.setItem('should_reload', value.toString());
  }

  private getShoudReload() {
    return localStorage.getItem('should_reload') === 'true';
  }

  public async loginAsOnCustomer(customerId: string, onLoginOtherEndLoad?: Function) {
    try {
      const atts = this.userHelperService.getUserAttributes();
      this.setLoginLogoutOtherProcessing(true);
      const response = await firstValueFrom(this.authService.signinOtherByCustomer(customerId));
      const hasResponse = !!response;
      onLoginOtherEndLoad!(hasResponse, '');
      if (hasResponse) {
        await this.overrideUserCache(response);
        this.setLoginLogoutOtherProcessing(false);
        this.setToReload();
      }
    } catch (err: any) {
      // clear the in-progress flag on error, else the next bootstrap sees it stuck
      // and force-signs-out (constructor guard) even though this was a clean failure.
      this.setLoginLogoutOtherProcessing(false);
      onLoginOtherEndLoad!(false, err?.error?.message);
    }
  }

  public async overrideUserCache(userExternal: any) {
    if (!userExternal) {
      return;
    }
    const attributes = userExternal.attributes;
    const currentAttributes = this.userHelperService.getUserAttributes();
    if (currentAttributes) {
      const backupAtts = Object.assign({}, currentAttributes);
      for (let key in attributes) {
        currentAttributes[key] = attributes[key];
      }
      currentAttributes.admin_user = {
        attributes: backupAtts,
        preferences: await this.userPreferencesService.getPreferences(),
      };
      await this.userHelperService.setUserAttributes(<any>currentAttributes, true);
      if (userExternal.userPreferences) {
        this.userPreferencesService.setupPreferences(userExternal.userPreferences);
      }
      if (userExternal.roles) {
        this.aclService.saveRoles(userExternal.roles, attributes['custom:userId']);
      }
    }
  }

  public async logoutOther(onEndLoad?: Function) {
    let remoteSignoutDone = false;
    let previousAttributes: any;
    try {
      const attributes: UserAttributesI = <UserAttributesI>this.userHelperService.getUserAttributes();
      const admin_user = attributes?.admin_user;
      previousAttributes = admin_user?.attributes;

      const refreshToken = this.userHelperService.getRefreshToken();
      const userLoginData = this.userHelperService.getUserLoginData();
      const token = this.userHelperService.getIdToken();
      if (!refreshToken) {
        return;
      }
      const refreshTokenData: RefreshTokenDataI = {
        email: previousAttributes.email,
        refreshToken,
        userLoginData,
        token,
      };
      const userId = previousAttributes['custom:userId'];
      const userIdOther = attributes['custom:userId'];
      const payload: SignOutDataI = { userIdOther, userId, refreshTokenData };
      this.setLoginLogoutOtherProcessing(true);
      const updatedToken = await firstValueFrom(this.authService.signoutOther(payload));
      remoteSignoutDone = true;
      await this.userHelperService.saveDataToken(updatedToken);
      this.userHelperService.updateUser();
      const chatSocketService = this.injector.get(ChatSocketService);
      chatSocketService.restart();
      const updatedUserAttributes = <UserAttributesI>this.userHelperService.getUserAttributes();
      if (updatedToken.userPreferences) {
        this.userPreferencesService.setupPreferences(updatedToken.userPreferences);
      }
      this.aclService.refresh(updatedUserAttributes['custom:userId']);
      this.setLoginLogoutOtherProcessing(false);
      UserHelperService.notifyLogout();
      onEndLoad!(true);
      this.setToReload();
    } catch (err: any) {
      // Never strand the in-progress flag: a stuck flag makes the next boot's constructor
      // guard force a full signout. On a post-remote-signout failure, restore the admin
      // session from the overlay we already hold (setUserAttributes no longer throws on a
      // team-default blip) and reload, instead of signing out. Clear the flag in every path.
      if (remoteSignoutDone && previousAttributes) {
        try {
          await this.userHelperService.setUserAttributes(previousAttributes);
          this.setLoginLogoutOtherProcessing(false);
          onEndLoad!(true);
          this.setToReload();
          return;
        } catch {
          // restore failed — fall through to the safe report path below
        }
      }
      this.setLoginLogoutOtherProcessing(false);
      onEndLoad!(false, err?.error?.message ?? err?.message ?? JSON.stringify(err));
    }
  }
}
